import { useCallback, useEffect, useRef, useState } from "react";
import type { LoadingStep, ProductionPlanColumnMapping, ProductionPlanInspection } from "./plan-import-types";
import { planImportGateway } from "./plan-import-gateway";
import type { ChangeEvent } from "react";
import type { PlanImportSharedState } from "./plan-import-workspace-contract";
import type { usePlanRequestWorkspace } from "./usePlanRequestWorkspace";
export function usePlanLibraryWorkspace({ selectedPlanId, setSelectedPlanId, savedPlans, setSavedPlans, setPreview, setError, setActiveTab, setPage, applyPreview, setSelectedMaterialCodes, setActiveRequests, setRequestReviewOpen, setRequestDrafts, notify, onDraftImported }: Pick<PlanImportSharedState, "selectedPlanId" | "setSelectedPlanId" | "savedPlans" | "setSavedPlans" | "setPreview" | "setError" | "setActiveTab" | "setPage"> & Pick<ReturnType<typeof usePlanRequestWorkspace>, "applyPreview" | "setSelectedMaterialCodes" | "setActiveRequests" | "setRequestReviewOpen" | "setRequestDrafts"> & {
    notify?: (message: string) => void;
    onDraftImported?: (weekStart: string) => void;
}) {
    const [file, setFile] = useState<File | null>(null);
    const [inspection, setInspection] = useState<ProductionPlanInspection | null>(null);
    const [mapping, setMapping] = useState<ProductionPlanColumnMapping | null>(null);
    const [loadingStep, setLoadingStep] = useState<LoadingStep>(null);
    const [plansLoading, setPlansLoading] = useState(true);
    const [uploadOpen, setUploadOpen] = useState(false);
    const inspectionRunRef = useRef(0);
    const savedPlanRunRef = useRef(0);
    const unsavedWorkbookRef = useRef(false);
    const loadSavedPlan = useCallback(async (planId: string) => {
        if (unsavedWorkbookRef.current && !window.confirm("Excel düzeltmeleri kaydedilmedi. Taslaktan ayrılmak istiyor musunuz?"))
            return;
        const runId = ++savedPlanRunRef.current;
        setPlansLoading(true);
        setError("");
        try {
            const result = await planImportGateway.loadSavedPlan(planId);
            if (runId !== savedPlanRunRef.current)
                return;
            setSelectedPlanId(result.plan.id);
            setFile(null);
            setInspection(null);
            setMapping(null);
            setUploadOpen(false);
            applyPreview(result.preview);
        }
        catch (requestError) {
            if (runId !== savedPlanRunRef.current)
                return;
            setError(requestError instanceof Error ? requestError.message : "Kayıtlı üretim planı açılamadı.");
        }
        finally {
            if (runId === savedPlanRunRef.current)
                setPlansLoading(false);
        }
    }, [applyPreview]);
    const persistProductionPlan = async (selectedFile: File, inspectedPlan: ProductionPlanInspection, selectedMapping: ProductionPlanColumnMapping) => {
        const weekStart = inspectedPlan.weekStart;
        if (!weekStart) {
            setError("Excel planının haftası belirlenemedi. Tarih başlıklarını kontrol edin.");
            return;
        }
        const body = new FormData();
        body.set("file", selectedFile);
        body.set("mapping", JSON.stringify(selectedMapping));
        body.set("dayStartsAt", inspectedPlan.calendar.dayStartsAt);
        setLoadingStep("CALCULATE");
        setError("");
        try {
            const existingDraft = await planImportGateway.weeklyDraftForWeek(weekStart);
            if (existingDraft) {
                if (!window.confirm("Bu haftanın kayıtlı taslağı Excel içeriğiyle değiştirilecek. Taslaktaki mevcut düzenlemeleri gözden geçirdiniz mi?"))
                    return;
                body.set("expectedPlanId", existingDraft.id);
                body.set("expectedRevision", String(existingDraft.revision));
            }
            const result = await planImportGateway.importWeeklyDraft(body);
            setUploadOpen(false);
            setFile(null);
            setInspection(null);
            setMapping(null);
            setPreview(null);
            const incomplete = result.importSummary.incompleteRowCount + result.importSummary.dependencyBlockedRowCount;
            notify?.(`Excel ${result.plan.weekStart} haftasının düzenlenebilir taslağına aktarıldı. ${result.importSummary.readyRowCount} satır hazır; ${incomplete} satır planlama düzenlemesi bekliyor.`);
            onDraftImported?.(result.plan.weekStart);
        }
        catch (requestError) {
            setPreview(null);
            setError(requestError instanceof Error ? requestError.message : "Üretim planı hesaplanamadı.");
        }
        finally {
            setLoadingStep(null);
        }
    };
    const inspectFile = async (mappingOverride?: ProductionPlanColumnMapping | null, fileOverride?: File, dayStartsAt: "00:00" | "08:00" = inspection?.calendar.dayStartsAt ?? "00:00") => {
        const selectedFile = fileOverride ?? file;
        const runId = inspectionRunRef.current + 1;
        inspectionRunRef.current = runId;
        if (!selectedFile) {
            setError("Önce haftalık üretim planı dosyasını seçin.");
            return;
        }
        const body = new FormData();
        body.set("file", selectedFile);
        body.set("dayStartsAt", dayStartsAt);
        if (mappingOverride)
            body.set("mapping", JSON.stringify(mappingOverride));
        setLoadingStep("INSPECT");
        setError("");
        try {
            let result: ProductionPlanInspection | null = null;
            for (let attempt = 0; attempt < 2; attempt += 1) {
                try {
                    result = await planImportGateway.inspect(body);
                    break;
                }
                catch (requestError) {
                    const message = requestError instanceof Error ? requestError.message : "";
                    const connectionInterrupted = message === "İşlem tamamlanamadı." ||
                        message.startsWith("Sunucuya bağlanılamadı");
                    if (attempt === 1 || !connectionInterrupted)
                        throw requestError;
                    await new Promise((resolve) => window.setTimeout(resolve, 350));
                }
            }
            if (!result)
                throw new Error("Dosya kontrolü tamamlanamadı.");
            if (inspectionRunRef.current !== runId)
                return;
            setInspection(result);
            setMapping(result.mapping);
            setPreview(null);
            setActiveTab("SHORTAGES");
            setPage(1);
        }
        catch (requestError) {
            if (inspectionRunRef.current !== runId)
                return;
            setInspection(null);
            setPreview(null);
            setError(requestError instanceof Error
                ? requestError.message
                : "Üretim planı formatı incelenemedi.");
        }
        finally {
            if (inspectionRunRef.current === runId)
                setLoadingStep(null);
        }
    };
    const calculate = async () => {
        if (!file || !inspection || !inspection.weekStart || !mapping || inspection.missingFields.length > 0)
            return;
        await persistProductionPlan(file, inspection, mapping);
    };
    const startNewWeek = () => {
        if (unsavedWorkbookRef.current && !window.confirm("Excel düzeltmeleri kaydedilmedi. Yeni dosyaya geçmek istiyor musunuz?"))
            return;
        ++savedPlanRunRef.current;
        setUploadOpen(true);
        setFile(null);
        setInspection(null);
        setMapping(null);
        setPreview(null);
        setError("");
        setSelectedMaterialCodes(new Set());
        setSelectedPlanId("");
    };
    const cancelNewWeek = () => {
        const target = selectedPlanId || savedPlans[0]?.id;
        if (target)
            void loadSavedPlan(target);
    };
    const refreshSelectedPlan = async () => {
        if (!selectedPlanId)
            return;
        setLoadingStep("CALCULATE");
        setError("");
        try {
            const result = await planImportGateway.refreshSavedPlan(selectedPlanId);
            applyPreview(result.preview);
            setSavedPlans((current) => current.map((item) => item.id === result.plan.id ? result.plan : item));
            notify?.("Plan güncel Mikro stok ve sipariş verileriyle yenilendi.");
        }
        catch (requestError) {
            setError(requestError instanceof Error ? requestError.message : "Plan yenilenemedi.");
        }
        finally {
            setLoadingStep(null);
        }
    };
    useEffect(() => {
        let active = true;
        void planImportGateway.listSavedPlans().then(({ items }) => {
            if (!active)
                return;
            setSavedPlans(items);
            if (items[0])
                void loadSavedPlan(items[0].id);
            else {
                setUploadOpen(true);
                setPlansLoading(false);
            }
        }).catch((requestError: unknown) => {
            if (!active)
                return;
            setUploadOpen(true);
            setPlansLoading(false);
            setError(requestError instanceof Error ? requestError.message : "Kayıtlı planlar alınamadı.");
        });
        return () => { active = false; };
    }, [loadSavedPlan]);
    const selectFile = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0] ?? null;
        event.target.value = "";
        if (!selectedFile)
            return;
        if (unsavedWorkbookRef.current && !window.confirm("Excel düzeltmeleri kaydedilmedi. Seçtiğiniz dosyayla değiştirmek istiyor musunuz?"))
            return;
        if (!/\.xlsx$/i.test(selectedFile.name) || selectedFile.size > 10 * 1024 * 1024) {
            setError("En fazla 10 MB büyüklüğünde bir .xlsx dosyası seçin.");
            return;
        }
        inspectionRunRef.current += 1;
        setSelectedPlanId("");
        setFile(selectedFile);
        setInspection(null);
        setMapping(null);
        setPreview(null);
        setLoadingStep(null);
        setSelectedMaterialCodes(new Set());
        setActiveRequests({});
        setRequestReviewOpen(false);
        setRequestDrafts([]);
        setError("");
        if (selectedFile)
            void inspectFile(undefined, selectedFile, "00:00");
    };
    return { file, inspection, mapping, loadingStep, plansLoading, uploadOpen, loadSavedPlan, inspectFile, calculate, startNewWeek, cancelNewWeek, refreshSelectedPlan, setFile, setUploadOpen, setInspection, unsavedWorkbookRef, selectFile };
}
